import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Separator } from "@shared/ui/separator";
import {
  AlertTriangle,
  Banknote,
  Check,
  CreditCard,
  FileText,
  Gauge,
  Gift,
  LayoutTemplate,
  RefreshCw,
  Search,
  Wrench,
} from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { cn } from "@shared/lib/utils";
import { useBillCorrectionTypes, useInitiateBillCorrection, usePatchBillCorrection } from "../hook";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface StepHelpers {
  getStepData: (index: number) => any;
  setStepData: (index: number, data: any) => void;
}

interface CorrectionTypesProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

// Helper function to get icon and colors based on correction type title
const getIconConfig = (title: string) => {
  const key = title.toLowerCase();

  switch (true) {
    case key.includes("consumption"):
      return {
        icon: Gauge,
        iconColor: "text-blue-600",
        iconBg: "bg-blue-50",
      };

    case key.includes("service charge"):
      return {
        icon: Wrench,
        iconColor: "text-purple-600",
        iconBg: "bg-purple-50",
      };

    case key.includes("penalty"):
    case key.includes("late fee"):
      return {
        icon: AlertTriangle,
        iconColor: "text-red-600",
        iconBg: "bg-red-50",
      };

    case key.includes("data correction"):
      return {
        icon: FileText,
        iconColor: "text-cyan-600",
        iconBg: "bg-cyan-50",
      };

    case key.includes("template"):
      return {
        icon: LayoutTemplate,
        iconColor: "text-indigo-600",
        iconBg: "bg-indigo-50",
      };

    default:
      return {
        icon: FileText,
        iconColor: "text-gray-600",
        iconBg: "bg-gray-50",
      };
  }
};

const CorrectionTypes: React.FC<CorrectionTypesProps> = ({
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { mutate: createBillCorrectionStep1 } = useInitiateBillCorrection();
  const { data: correctionTypes } = useBillCorrectionTypes();
  const selectedBill = stepHelpers?.getStepData(0);
  const selectedType = stepHelpers?.getStepData(1);
  const [selectedCorrectionTypes, setSelectedCorrectionTypes] = useState<
  number[]
  >(selectedType?.selectedCorrectionTypes || []);
  
  const {mutate: updateCorrectionTypes} = usePatchBillCorrection();
  const handleContinue = () => {
    if (selectedType?.selectedCorrectionTypes) {
      updateCorrectionTypes({
        id: selectedBill?.correctionId,
        type: 'modify',
        payload: {correctionTypes: selectedCorrectionTypes},
      })
      onNext?.();
      stepHelpers?.setStepData(currentStepIndex, {
        selectedCorrectionTypes,
      });
    } else {
      createBillCorrectionStep1(
        {
          remoteUtilityId,
          correctionTypes: selectedCorrectionTypes,
          billId: selectedBill?.selectedBill?.id,
        },
        {
          onSuccess: (res) => {
            const correctionId = res?.result ? res.result?.correctionId : res.id;
            stepHelpers?.setStepData(0, {...selectedBill, correctionId})
            onNext?.();
            stepHelpers?.setStepData(currentStepIndex, {
              selectedCorrectionTypes,
            });
          },
        }
      );
    }
  };

  const toggleCorrectionType = (key: number) => {
    setSelectedCorrectionTypes((prev) => {
      const current = prev || [];
      return current.includes(key)
        ? current.filter((typeKey) => typeKey !== key)
        : [...current, key];
    });
  };

  return (
    <div className="flex flex-col h-full gap-6">
      <Card className="border-none p-0 shadow-sm">
        <CardHeader className="pb-4 border-b border-gray-100">
          <CardTitle className="text-base font-semibold text-gray-900">
            Select Correction Types
          </CardTitle>
          <p className="text-sm text-gray-500">
            Choose one or more correction types you need to perform. You can
            apply multiple corrections to the same bill.
          </p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {correctionTypes?.map((type) => {
              const iconConfig = getIconConfig(type.title);
              const IconComponent = iconConfig.icon;

              return (
                <div
                  key={type.key}
                  onClick={() => toggleCorrectionType(type.key)}
                  className={cn(
                    "relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-md",
                    selectedCorrectionTypes?.includes(type.key)
                      ? "border-blue-600 bg-blue-50/30"
                      : "border-gray-100 bg-white hover:border-blue-200"
                  )}
                >
                  <div
                    className={cn(
                      "h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0",
                      iconConfig.iconBg
                    )}
                  >
                    <IconComponent
                      className={cn("h-6 w-6", iconConfig.iconColor)}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {type.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {type.description}
                    </p>
                  </div>
                  {selectedCorrectionTypes?.includes(type.key) && (
                    <div className="absolute top-4 right-4">
                      <div className="h-5 w-5 bg-blue-600 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedCorrectionTypes?.length}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default CorrectionTypes;
